import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
                     'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const navBtnStyle = {
  width: 28, height: 28, border: '1px solid #D8DDDA', background: '#fff',
  '--ch': '5px', '--stroke': '#D8DDDA', cursor: 'pointer', fontSize: 16, display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: '#1C3D2E', fontWeight: 600
};

// anchorEl — ref на кнопку-триггер; когда передан, рендерится через portal с position: fixed
function DateRangeCalendar({ dateFrom, dateTo, onSelect, onClose, anchorEl }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (dateTo) {
      const [y, m] = dateTo.split('-').map(Number);
      return new Date(y, m - 1);
    }
    return new Date();
  });
  const [selecting, setSelecting] = useState(dateFrom && dateTo ? 'done' : 'from');
  const [tempFrom, setTempFrom] = useState(dateFrom || '');
  const [tempTo, setTempTo] = useState(dateTo || '');
  const [hovered, setHovered] = useState(null);
  const [fixedPos, setFixedPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (anchorEl?.current) {
      const rect = anchorEl.current.getBoundingClientRect();
      setFixedPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [anchorEl]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Escape закрывает календарь (UX-аудит 25.10); перехват раньше Modal — окно под ним остаётся
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const fmtDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = [];
    let startDay = first.getDay() - 1;
    if (startDay < 0) startDay = 6;
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= last.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  const prevYear = () => setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth()));
  const nextYear = () => setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth()));

  const [yearPickerOpen, setYearPickerOpen] = useState(false);

  const handleDayClick = (date) => {
    if (!date) return;
    const val = fmtDate(date);
    if (selecting === 'done' || selecting === 'from') {
      setTempFrom(val);
      setTempTo('');
      setSelecting('to');
    } else {
      if (val < tempFrom) {
        setTempFrom(val);
        setTempTo('');
        setSelecting('to');
      } else {
        setTempTo(val);
        setSelecting('done');
        onSelect(tempFrom, val);
      }
    }
  };

  const isInRange = (date) => {
    if (!date || !tempFrom) return false;
    const val = fmtDate(date);
    if (tempTo) return val >= tempFrom && val <= tempTo;
    if (selecting === 'to' && hovered) {
      const hovDate = fmtDate(hovered);
      const rangeEnd = hovDate >= tempFrom ? hovDate : tempFrom;
      const rangeStart = hovDate >= tempFrom ? tempFrom : hovDate;
      return val >= rangeStart && val <= rangeEnd;
    }
    return false;
  };

  const isStart = (date) => date && fmtDate(date) === tempFrom;
  const isEnd = (date) => date && tempTo && fmtDate(date) === tempTo;
  const isToday = (date) => date && fmtDate(date) === fmtDate(new Date());

  const days = getDays();

  const fmtDisplay = (dateStr) => {
    if (!dateStr) return '...';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  const content = (
    <div ref={ref} style={
      anchorEl ? {
        position: 'fixed', top: fixedPos.top, left: fixedPos.left, zIndex: 1500,
        background: '#fff', '--ch': '8px', '--stroke': '#D8DDDA', padding: 14,
        border: '1px solid #D8DDDA', boxShadow: '0 4px 16px rgba(28,61,46,0.10)',
        width: 300, userSelect: 'none',
      } : {
        position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 4,
        background: '#fff', '--ch': '8px', '--stroke': '#D8DDDA', padding: 14,
        border: '1px solid #D8DDDA', boxShadow: '0 4px 16px rgba(28,61,46,0.10)',
        width: 300, userSelect: 'none',
      }
    }>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={prevYear} style={navBtnStyle} title="Предыдущий год">«</button>
          <button type="button" onClick={prevMonth} style={navBtnStyle} title="Предыдущий месяц">‹</button>
        </div>
        <button
          type="button"
          onClick={() => setYearPickerOpen(v => !v)}
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            fontWeight: 600, fontSize: 14, color: '#1C3D2E',
            padding: '4px 10px', '--ch': '5px',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#DFF2E8'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </button>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={nextMonth} style={navBtnStyle} title="Следующий месяц">›</button>
          <button type="button" onClick={nextYear} style={navBtnStyle} title="Следующий год">»</button>
        </div>
      </div>

      {yearPickerOpen && (
        <div style={{
          marginBottom: 10, padding: 8,
          background: '#F9F9F6', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA',
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4,
          maxHeight: 180, overflowY: 'auto',
        }}>
          {(() => {
            const cy = currentMonth.getFullYear();
            const years = [];
            for (let y = cy - 5; y <= cy + 5; y++) years.push(y);
            return years.map(y => (
              <button
                key={y}
                type="button"
                onClick={() => { setCurrentMonth(new Date(y, currentMonth.getMonth())); setYearPickerOpen(false); }}
                style={{
                  border: 'none', '--ch': '5px', padding: '6px 4px', cursor: 'pointer',
                  fontSize: 13, fontWeight: y === cy ? 700 : 500,
                  background: y === cy ? '#1C3D2E' : '#fff',
                  color: y === cy ? '#fff' : '#1C3D2E',
                }}
              >
                {y}
              </button>
            ));
          })()}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#5E6561', fontWeight: 500, padding: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {days.map((date, i) => {
          const inRange = isInRange(date);
          const start = isStart(date);
          const end = isEnd(date);
          const today = isToday(date);
          return (
            <div
              key={i}
              onClick={() => handleDayClick(date)}
              onMouseEnter={() => date && setHovered(date)}
              onMouseLeave={() => setHovered(null)}
              style={{
                aspectRatio: '1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, '--ch': '5px', '--stroke': (today && !start && !end ? '#1C3D2E' : 'transparent'),
                cursor: !date ? 'default' : 'pointer',
                color: !date ? 'transparent' : (start || end) ? '#fff' : '#1C3D2E',
                background: (start || end) ? '#1C3D2E' : inRange ? '#DFF2E8' : 'transparent',
                fontWeight: (start || end || today) ? 700 : 400,
                border: today && !start && !end ? '1px solid #1C3D2E' : '1px solid transparent',
                transition: 'background 0.15s'
              }}
            >
              {date ? date.getDate() : ''}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: '#5E6561', textAlign: 'center' }}>
        {tempFrom && !tempTo && selecting === 'to'
          ? `${fmtDisplay(tempFrom)} — выберите конец`
          : tempFrom && tempTo
          ? `${fmtDisplay(tempFrom)} — ${fmtDisplay(tempTo)}`
          : 'Выберите начало периода'}
      </div>
    </div>
  );

  return anchorEl ? createPortal(content, document.body) : content;
}

export default DateRangeCalendar;
